import React, { useState, useEffect } from 'react';
import { CheckCircle, Circle, Briefcase, Wrench } from 'lucide-react';
import { getTasks, updateTask } from '../lib/supabase/tasks';
import { useEntity } from '../hooks/useEntity';
import type { TaskWithProject } from '../lib/supabase/tasks';

interface TaskListProps {
  department?: string;
  limit?: number;
  showAddButton?: boolean;
}

export default function TaskList({ department, limit, showAddButton = true }: TaskListProps) {
  const [tasks, setTasks] = useState<TaskWithProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { entity } = useEntity();

  useEffect(() => {
    if (entity?.id) {
      loadTasks();
    }
  }, [department, entity?.id]);

  const loadTasks = async () => {
    if (!entity?.id) return;
    
    try {
      setLoading(true);
      setError(null);
      try {
        const fetchedTasks = await getTasks(department, entity.id, true);
        setTasks(limit ? fetchedTasks.slice(0, limit) : fetchedTasks);
      } catch (err) {
        console.error('Failed to load tasks:', err);
        setError('Failed to load tasks. Please try again.');
      }
    } catch (err) {
      console.error('Failed to load tasks:', err);
      setError('Failed to load tasks. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleTaskStatus = async (taskId: string, currentStatus: string) => {
    try {
      const newStatus = currentStatus === 'completed' ? 'pending' : 'completed';
      
      // Check if this is a project task that doesn't have a corresponding operation task
      const task = tasks.find(t => t.id === taskId);
      if (task?.isProjectTask) {
        await updateTask(taskId, { status: newStatus, isProjectTask: true });
      } else {
        await updateTask(taskId, { status: newStatus });
      }
      
      // Update local state
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === taskId ? { ...task, status: newStatus } : task
        )
      );
    } catch (error) {
      console.error('Failed to update task status:', error);
    }
  };

  const priorityColors = {
    High: 'text-red-500',
    Medium: 'text-yellow-500',
    Low: 'text-green-500',
    high: 'text-red-500',
    medium: 'text-yellow-500',
    low: 'text-green-500',
  };

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-4 text-red-500">
        <p>{error}</p>
        <button 
          onClick={loadTasks}
          className="text-blue-500 hover:text-blue-600 mt-2"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {tasks.length === 0 ? (
        <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg">
          <p>No tasks found. {showAddButton ? 'Add your first task to get started.' : 'Tasks will appear here when created.'}</p>
        </div>
      ) : (
        tasks.map((task) => (
          <div key={task.id} className="flex items-start space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
            <button 
              className="flex-shrink-0 mt-0.5"
              onClick={() => toggleTaskStatus(task.id, task.status)}
            >
              {task.status === 'completed' ? (
                <CheckCircle className="w-5 h-5 text-blue-500" />
              ) : (
                <Circle className="w-5 h-5 text-gray-300" />
              )}
            </button>
            <div className="flex-grow">
              <p className={`text-gray-900 ${task.status === 'completed' ? 'line-through' : ''}`}>
                {task.title}
              </p>
              <div className="flex items-center space-x-2">
                <span className={`text-sm ${priorityColors[task.priority as keyof typeof priorityColors]}`}>
                  {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)} Priority
                </span>
                {task.project && (
                  <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full flex items-center ml-2">
                    {task.department_origin === 'workshop' ? (
                      <Wrench className="w-3 h-3 mr-1" />
                    ) : (
                      <Briefcase className="w-3 h-3 mr-1" />
                    )}
                    {task.project.name}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))
      )}
      {showAddButton && (
        <button className="w-full text-blue-500 hover:text-blue-600 text-sm py-2">
          + Add New Task
        </button>
      )}
    </div>
  );
}